import { Button, Box } from '@mui/material'
import { getCSvConfigDialoDispAndCache, downloadCsvFile } from 'api/csv'
import { useRootLoaderData } from 'components/Root.loader'
import { DOC_TYPE, MAX_MUTLTI_EDIT_DOCS } from 'config'
import { DocListItem } from 'entities/Documents'
import { useAlert, useConfirm } from 'hooks/useDialog'
import useEditDocuments from 'hooks/useEditDocuments'
import { useMemo } from 'react'

type Props = {
  selectedDocs: DocListItem[]
  selectedIDs: string[]
  setOpenAssignDialog: (x: boolean) => void
  setOpenDelDialog: (x: boolean) => void
  setOpenSendDialog: (x: boolean) => void
  setOpenCsvConfigDialog: (x: boolean) => void
  setOpenUploadDialog: (x: boolean) => void
  reloadDocs: () => void
}

const mode = 'normal'

function ControlArea({
  selectedDocs,
  selectedIDs,
  setOpenAssignDialog,
  setOpenDelDialog,
  setOpenSendDialog,
  setOpenCsvConfigDialog,
  setOpenUploadDialog,
  reloadDocs,
}: Props) {
  const alert = useAlert()
  const confirm = useConfirm()
  const editDocuments = useEditDocuments()

  const selectedTypes = useMemo(() => {
    return [...new Set(selectedDocs.map(({ documentTypeName }) => documentTypeName))]
  }, [selectedDocs])

  const rootData = useRootLoaderData()
  const { useConcur } = rootData.userGroupConfig
  const disabled = selectedIDs.length === 0
  const maxMultiEdit = selectedIDs.length > MAX_MUTLTI_EDIT_DOCS

  const multiTypesSelected = selectedTypes.length > 1
  const invoiceOnlySelected = !multiTypesSelected && selectedTypes[0] === DOC_TYPE.INVOICE

  const handleAssignButtonClick = () => {
    setOpenAssignDialog(true)
  }

  const handleDeleteButtonClick = () => {
    setOpenDelDialog(true)
  }

  const handleSendButtonClick = () => {
    setOpenSendDialog(true)
  }

  const handleEditButtonClick = () => {
    if (multiTypesSelected) {
      return alert({ message: '異なる文書タイプを同時に編集はできません' })
    }
    editDocuments(
      mode,
      selectedDocs.map(({ objectID }) => objectID),
      true,
    )
  }

  const handleDownloadButtonClick = async () => {
    if (multiTypesSelected) {
      return alert({ message: '異なる文書タイプを同時にCSV出力はできません' })
    }

    const docType = selectedDocs[0].documentType

    let csvConfigDialogDisp = true
    try {
      csvConfigDialogDisp = await getCSvConfigDialoDispAndCache(
        docType,
        undefined, // subgroup is only used in verify mode
        selectedDocs[0].ocrBuilderConfigID,
      )
    } catch {
      return alert({ message: 'csv設定取得失敗しました。' })
    }

    if (csvConfigDialogDisp && docType !== DOC_TYPE.GENERAL_OCR) {
      setOpenCsvConfigDialog(true) // can't await dialog...
    } else {
      const { status } = await confirm({
        message: [
          '選択した文書の結果ファイルをダウンロードしますか？',
          '出力設定は設定画面より変更可能です',
        ],
      })
      if (status === 'canceled') {
        return
      }
      try {
        await downloadCsvFile(selectedIDs)
      } catch {
        alert({ message: 'ダウンロードに失敗しました。' })
      }
      reloadDocs()
    }
  }

  return (
    <Box sx={{ display: 'flex', mb: 2, justifyContent: 'space-between' }}>
      <div>
        <Button
          onClick={() => setOpenUploadDialog(true)}
          variant="outlined"
          color="primary"
          data-testid="home-control-register-button"
        >
          新規登録
        </Button>
        <Button
          onClick={handleAssignButtonClick}
          variant="outlined"
          disabled={disabled}
          data-testid="home-control-assign-button"
        >
          担当割付
        </Button>
      </div>
      <div style={{ textAlign: 'right' }}>
        <Button
          variant="outlined"
          disabled={disabled || maxMultiEdit}
          onClick={handleEditButtonClick}
          data-testid="home-control-edit-button"
        >
          編集
        </Button>
        <Button
          onClick={handleDownloadButtonClick}
          variant="contained"
          disabled={disabled || multiTypesSelected}
          data-testid="home-control-download-results-button"
        >
          結果のダウンロード
        </Button>
        {useConcur && (
          <Button
            onClick={handleSendButtonClick}
            variant="contained"
            disabled={disabled || !invoiceOnlySelected}
            data-testid="home-control-send-results-button"
          >
            結果の送信
          </Button>
        )}
        <Button
          onClick={handleDeleteButtonClick}
          variant="contained"
          color="secondary"
          disabled={disabled}
          data-testid="home-control-delete-button"
        >
          削除
        </Button>
      </div>
    </Box>
  )
}

export default ControlArea
